import { BIOMES, getZone, type Quest, TUTORIAL_ZONE_ID } from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import type { ContractBoard } from './contract-board.js';
import { isAction } from '../settings/keybindings.js';
import { closeButton, hudShortcutButton } from './hud.js';
import { hideItemHover } from './item-hover.js';
import { element, kitIcon, kitTitle } from './kit.js';
import type { QuestEntry, QuestLog } from './quest-log.js';
import { questRewards } from './quest-rewards.js';
import { npcName, objectiveLine } from './quest-text.js';
import { uiFormat, uiText } from './text.js';
import './quests.css';

/**
 * The quest journal (ADR 0047): a HUD button (key U) and a window with the
 * quests under way, each objective's count, who takes it back and what it
 * pays, and those already turned in; a second tab holds the day's and the
 * week's contracts (ADR 0051). Only a view of what the server sent;
 * abandoning is an intention, confirmed by a second click.
 */

type JournalTab = 'quests' | 'contracts';

export interface QuestJournalOptions {
  log: QuestLog;
  icons: () => CssAtlas | null;
  onAbandon: (questId: string) => void;
  contracts: ContractBoard;
}

export class QuestJournal {
  private readonly root = element('section', 'quest-journal');
  private readonly list = element('div', 'quest-journal__list');
  private readonly detail = element('div', 'quest-journal__detail');
  private readonly count = element('span', 'quest-journal__count');
  private readonly body = element('div', 'quest-journal__body');
  private readonly tabs = new Map<JournalTab, HTMLButtonElement>();
  private tab: JournalTab = 'quests';
  private readonly button: HTMLButtonElement;
  /** Contracts filled and not cashed yet, counted on the HUD button (ADR 0051). */
  private readonly badge = element('span', 'quest-journal__badge');
  private selected: string | undefined;
  /** Upright on a phone the list and the detail take turns: true while a quest is read. */
  private reading = false;
  /** The quest whose « Abandonner » was clicked once: a second click confirms. */
  private abandoning: string | undefined;
  private readonly offLog: () => void;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    if (document.activeElement instanceof HTMLTextAreaElement) return;
    if (event.key === 'Escape' && !this.root.hidden) {
      this.close();
      return;
    }
    if (!isAction(event, 'quests')) return;
    event.preventDefault();
    this.toggle();
  };

  constructor(private readonly options: QuestJournalOptions) {
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Journal de quêtes'));
    const title = element('h2', 'quest-journal__title', 'Journal de quêtes');
    kitTitle(title, 'quests');
    title.append(
      this.count,
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );
    const tabs = element('div', 'auction__tabs quest-journal__tabs');
    for (const [id, label] of [
      ['quests', 'Quêtes'],
      ['contracts', 'Contrats'],
    ] as const) {
      const tab = element('button', 'auction__tab', label);
      tab.type = 'button';
      tab.addEventListener('click', () => {
        this.show(id);
      });
      this.tabs.set(id, tab);
      tabs.append(tab);
    }
    this.body.append(this.list, this.detail);
    this.root.append(title, tabs, this.body, options.contracts.root);
    document.body.append(this.root);

    this.button = hudShortcutButton('quests', 'Quêtes', 'quests');
    this.badge.hidden = true;
    this.badge.setAttribute('aria-hidden', 'true');
    this.button.append(this.badge);
    this.button.addEventListener('click', () => {
      this.toggle();
    });
    window.addEventListener('keydown', this.onKeyDown);
    this.offLog = options.log.onChange(() => {
      this.render();
    });
    this.render();
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    if (this.root.hidden) this.open();
    else this.close();
  }

  /** Open on a quest, when the tracker's line is clicked, or on a tab. */
  open(questId?: string, tab: JournalTab = 'quests'): void {
    if (questId !== undefined) this.selected = questId;
    this.reading = questId !== undefined;
    this.abandoning = undefined;
    this.root.hidden = false;
    this.show(tab);
  }

  close(): void {
    hideItemHover();
    this.root.hidden = true;
    this.options.contracts.setVisible(false);
  }

  private show(tab: JournalTab): void {
    this.tab = tab;
    this.body.hidden = tab !== 'quests';
    this.options.contracts.root.hidden = tab !== 'contracts';
    this.options.contracts.setVisible(tab === 'contracts' && !this.root.hidden);
    this.render();
  }

  /** The contracts moved: the tab's count follows, whichever tab is shown. */
  refreshTabs(): void {
    const ready = this.options.contracts.ready;
    this.badge.hidden = ready === 0;
    this.badge.textContent = String(ready);
    // On a phone the button waits in the drawer: the menu button carries the count.
    document.body.classList.toggle('contract-ready', ready > 0);
    document.querySelector('.hud-menu')?.setAttribute('data-contracts', String(ready));
    for (const [id, tab] of this.tabs) {
      tab.classList.toggle('auction__tab--active', id === this.tab);
      const label = id === 'quests' ? 'Quêtes' : 'Contrats';
      tab.textContent =
        id === 'contracts' && ready > 0
          ? uiFormat('Contrats ({count})', { count: ready })
          : uiText(label);
    }
  }

  private render(): void {
    this.refreshTabs();
    const log = this.options.log;
    const entries = log.entries();
    const finished = log.finished();
    this.count.textContent = entries.length > 0 ? ` · ${String(entries.length)}` : '';
    const known = new Set([
      ...entries.map((entry) => entry.quest.id),
      ...finished.map((quest) => quest.id),
    ]);
    if (this.selected === undefined || !known.has(this.selected)) {
      this.selected = entries[0]?.quest.id ?? finished.at(-1)?.id;
      this.reading = false;
    }

    this.list.replaceChildren();
    if (entries.length === 0) {
      this.list.append(
        element(
          'p',
          'smith__muted',
          'Aucune quête en cours. Cherche le « ! » doré au-dessus des habitants.',
        ),
      );
    }
    for (const entry of entries) this.list.append(this.row(entry.quest, entry));
    if (finished.length > 0) {
      this.list.append(
        element(
          'h3',
          'quest-journal__section',
          uiFormat('Accomplies ({count})', { count: finished.length }),
        ),
      );
      for (const quest of finished) this.list.append(this.row(quest, undefined));
    }

    this.detail.replaceChildren();
    const quest =
      entries.find((entry) => entry.quest.id === this.selected)?.quest ??
      finished.find((done) => done.id === this.selected);
    if (quest !== undefined) this.renderDetail(quest, log.entry(quest.id));
    this.body.classList.toggle('quest-journal__body--reading', this.reading && quest !== undefined);
  }

  private row(quest: Quest, entry: QuestEntry | undefined): HTMLElement {
    const button = element('button', 'quest-journal__row');
    button.type = 'button';
    if (quest.id === this.selected) button.classList.add('quest-journal__row--on');
    const icon = kitIcon(entry === undefined || entry.complete ? 'quest-ready' : 'quest-available');
    if (entry === undefined) icon.classList.add('quest-mark--grey');
    button.append(icon, element('span', 'quest-journal__row-name', quest.name));
    if (entry?.complete === true)
      button.append(element('span', 'quest-journal__ready', 'À rendre'));
    button.addEventListener('click', () => {
      this.selected = quest.id;
      this.abandoning = undefined;
      this.reading = true;
      this.render();
      this.detail.scrollTop = 0;
    });
    return button;
  }

  private renderDetail(quest: Quest, entry: QuestEntry | undefined): void {
    const done = this.options.log.isDone(quest.id);
    const toList = element('button', 'quest-journal__to-list', 'Retour à la liste');
    toList.type = 'button';
    toList.addEventListener('click', () => {
      this.reading = false;
      this.abandoning = undefined;
      this.render();
      this.list.querySelector('.quest-journal__row--on')?.scrollIntoView({ block: 'nearest' });
    });
    this.detail.append(toList);
    this.detail.append(element('h3', 'quest-journal__name', quest.name));
    this.detail.append(
      element(
        'p',
        'smith__muted',
        uiFormat('{region} · niveau {level}', { region: regionOf(quest), level: quest.level }),
      ),
    );
    this.detail.append(element('p', 'quest-journal__summary', quest.summary));
    if (!done) {
      const list = element('ul', 'quest-objectives');
      quest.objectives.forEach((_, index) => {
        const progress = entry?.progress[index];
        const item = element('li', 'quest-objectives__item', objectiveLine(quest, index, progress));
        if (progress !== undefined && progress.value >= progress.target) {
          item.classList.add('quest-objectives__item--done');
        }
        list.append(item);
      });
      this.detail.append(list);
      const back =
        entry?.complete === true
          ? uiFormat('Terminée : retourne voir {npc}.', { npc: npcName(quest.turnIn.mob) })
          : uiFormat('À rendre à {npc} une fois terminée.', { npc: npcName(quest.turnIn.mob) });
      this.detail.append(element('p', 'quest-journal__back', back));
    } else {
      this.detail.append(element('p', 'quest-journal__back', 'Quête accomplie.'));
    }
    this.detail.append(questRewards(quest, this.options.icons()));
    // The camp's lessons are never given up (ADR 0078).
    if (entry !== undefined && !quest.prologue) {
      const confirming = this.abandoning === quest.id;
      const abandon = element(
        'button',
        'smith__go quest-journal__abandon',
        confirming ? 'Confirmer l’abandon' : 'Abandonner',
      );
      abandon.type = 'button';
      abandon.addEventListener('click', () => {
        if (this.abandoning !== quest.id) {
          this.abandoning = quest.id;
          this.render();
          return;
        }
        this.abandoning = undefined;
        this.options.onAbandon(quest.id);
      });
      this.detail.append(abandon);
    }
  }

  destroy(): void {
    this.offLog();
    window.removeEventListener('keydown', this.onKeyDown);
    this.button.remove();
    this.root.remove();
  }
}

/** Where a quest belongs: its region, or the camp for its prologue (ADR 0066). */
function regionOf(quest: Quest): string {
  if (quest.prologue) return getZone(TUTORIAL_ZONE_ID)?.name ?? BIOMES[quest.biome].name;
  return BIOMES[quest.biome].name;
}
